import React from "react"

import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

interface EventFiltersProps {
  selectedLocation: string
  setSelectedLocation: (location: string) => void
  selectedDate: Date | undefined
  setSelectedDate: (date: Date | undefined) => void
  nameSearch?: string
  setNameSearch?: (name: string) => void
  nameLabel?: string
  locations: { value: string; label: string }[]
  statusOptions?: { value: string; label: string }[]
  selectedStatus?: string
  setSelectedStatus?: (status: string) => void
  capacityOptions?: { value: string; label: string }[]
  selectedCapacity?: string
  setSelectedCapacity?: (capacity: string) => void
}

const EventFilters: React.FC<EventFiltersProps> = ({
  selectedLocation,
  setSelectedLocation,
  selectedDate,
  setSelectedDate,
  nameSearch = "",
  setNameSearch,
  nameLabel = "Driver Name",
  locations,
  statusOptions,
  selectedStatus,
  setSelectedStatus,
  capacityOptions,
  selectedCapacity,
  setSelectedCapacity,
}) => {
  return (
    <div className="bg-palette-jungleGreen000 p-3 rounded-md mb-6">
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
        {/* Location Filter */}
        <div className="space-y-2">
          <Label htmlFor="location" className="text-sm font-medium">
            Filter by Location
          </Label>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger id="location" className="h-10">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((location) => (
                <SelectItem key={location.value} value={location.value}>
                  {location.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Name Search */}
        {setNameSearch && (
          <div className="space-y-2">
            <Label htmlFor="nameSearch" className="text-sm font-medium">
              {nameLabel}
            </Label>
            <Input
              id="nameSearch"
              placeholder={`Search by ${nameLabel.toLowerCase()}`}
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              className="h-10"
            />
          </div>
        )}

        {/* Status Filter */}
        {statusOptions && setSelectedStatus && (
          <div className="space-y-2">
            <Label htmlFor="status" className="text-sm font-medium">
              Status
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger id="status" className="h-10">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Capacity Filter */}
        {capacityOptions && setSelectedCapacity && (
          <div className="space-y-2">
            <Label htmlFor="capacity" className="text-sm font-medium">
              Capacity
            </Label>
            <Select value={selectedCapacity} onValueChange={setSelectedCapacity}>
              <SelectTrigger id="capacity" className="h-10">
                <SelectValue placeholder="All Capacity" />
              </SelectTrigger>
              <SelectContent>
                {capacityOptions.map((capacity) => (
                  <SelectItem key={capacity.value} value={capacity.value}>
                    {capacity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Date Filter */}
        <div className="space-y-2">
          <Label htmlFor="date" className="text-sm font-medium">
            Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-10",
                  !selectedDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}

export default EventFilters
